<script setup>
import { h, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { registerLogoClick } from 'dashboard/composables/useEasterEgg';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { flattenPinnableItems } from 'dashboard/composables/usePinnedSidebarItems';

import SidebarGroup from './SidebarGroup.vue';
import SidebarPinnedSection from './SidebarPinnedSection.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import SidebarNotificationBell from './SidebarNotificationBell.vue';
import Logo from 'next/icon/Logo.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const { copilotMenuEnabled } = useConfig();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const getAccount = useMapGetter('accounts/getAccount');

// The support account itself shouldn't get a "Suporte" item pointing back at itself.
const isSupportAccount = computed(
  () => !!getAccount.value(accountId.value)?.is_support_account
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getAllUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getMentionsUnreadCount = useMapGetter(
  'conversationUnreadCounts/getMentionsUnreadCount'
);
const getParticipatingUnreadCount = useMapGetter(
  'conversationUnreadCounts/getParticipatingUnreadCount'
);
const getUnattendedUnreadCount = useMapGetter(
  'conversationUnreadCounts/getUnattendedUnreadCount'
);
const getCustomFilterUnreadCount = useMapGetter(
  'conversationUnreadCounts/getCustomFilterUnreadCount'
);
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const pipelinesStore = usePipelinesStore();
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const isAdministrator = computed(() => currentRole.value === 'administrator');

const TASK_STATS_POLL_INTERVAL_MS = 60000;
let taskStatsPollTimer = null;

const fetchTaskStats = () => {
  if (!currentUser.value?.id) return;
  store.dispatch('taskStats/get', currentUser.value.id);
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  pipelinesStore.get();
  fetchTaskStats();
  taskStatsPollTimer = setInterval(fetchTaskStats, TASK_STATS_POLL_INTERVAL_MS);
});

onUnmounted(() => {
  if (taskStatsPollTimer) clearInterval(taskStatsPollTimer);
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch(accountId, fetchTaskStats);

const normalizeUnreadCount = count => {
  const unreadCount = Number(count);
  return Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0;
};

const sortByUnreadCount = (items, labelKey, unreadCountKey) =>
  items.slice().sort((a, b) => {
    const unreadCountDiff =
      normalizeUnreadCount(unreadCountKey(b)) -
      normalizeUnreadCount(unreadCountKey(a));

    if (unreadCountDiff !== 0) return unreadCountDiff;

    return labelKey(a).localeCompare(labelKey(b));
  });

const sortedInboxes = computed(() =>
  sortByUnreadCount(
    inboxes.value,
    inbox => inbox.name,
    inbox => getInboxUnreadCount.value(inbox.id)
  )
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const pipelineMenuChildren = computed(() => {
  const children = [];
  [...pipelinesStore.getPipelinesList]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .forEach(pipeline => {
      children.push({
        name: `Pipeline ${pipeline.id}`,
        pinKey: `pipeline:${pipeline.id}`,
        label: pipeline.name,
        to: accountScopedRoute('deals_pipeline', { pipelineId: pipeline.id }),
        activeOn: ['deals_pipeline'],
      });
    });
  children.push({
    name: 'All Deals',
    label: t('SIDEBAR.ALL_DEALS'),
    to: accountScopedRoute('deals_all', {}, { page: 1, search: undefined }),
    activeOn: ['deals_all', 'deals_dashboard_index'],
  });
  return children;
});

// Itens ocultos do menu lateral por ainda não terem sido testados (specs 138/139).
// Só o menu some: as rotas e páginas continuam acessíveis pela URL.
// 'Campaigns' reexibido a pedido do dono (2026-10-07) para facilitar a validação em
// staging da spec 137; reocultar (adicionar 'Campaigns' de volta) se o dono pedir.
// O Copiloto só aparece quando a variável de ambiente ENABLE_COPILOT_MENU está definida
// (ligada em develop/staging; ausente em produção).
const HIDDEN_MENU_ITEMS = [
  'Portals',
  ...(copilotMenuEnabled ? [] : ['Captain']),
];
// Sub-itens ocultos de Campanhas: SMS, Chat ao vivo e a antiga página de campanhas de
// WhatsApp (que a spec 137 unifica na sub-página "WhatsApp"). Para reexibir, remova da lista.
const HIDDEN_MENU_CHILDREN = [
  'Live chat',
  'SMS',
  'WhatsApp Campaigns',
  // Configurações: itens ocultos por ora (Robôs, Macros, Auditoria, Funções Personalizadas, Cobrança).
  'Settings Agent Bots',
  'Settings Macros',
  'Settings Audit Logs',
  'Settings Custom Roles',
  'Settings Billing',
];

const menuItems = computed(() => {
  return [
    {
      name: 'Dashboard',
      label: t('SIDEBAR.DASHBOARD'),
      icon: 'i-lucide-layout-dashboard',
      children: [
        {
          name: 'Meu espaço',
          label: t('SIDEBAR.MEU_ESPACO'),
          to: accountScopedRoute('painel_meu_espaco'),
          activeOn: ['painel_meu_espaco'],
        },
        {
          name: 'Dashboard Atendimento',
          label: t('SIDEBAR.DASHBOARD_ATENDIMENTO'),
          to: accountScopedRoute('painel_atendimento'),
          activeOn: ['painel_atendimento'],
        },
        {
          name: 'Dashboard Comercial',
          label: t('SIDEBAR.DASHBOARD_COMERCIAL'),
          to: accountScopedRoute('painel_comercial'),
          activeOn: ['painel_comercial'],
        },
        ...(isAdministrator.value
          ? [
              {
                name: 'Live Conversations',
                label: t('SIDEBAR.LIVE_CONVERSATIONS'),
                to: accountScopedRoute('live_conversations_index'),
                activeOn: ['live_conversations_index'],
              },
            ]
          : []),
      ],
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      badgeCount: getAllUnreadCount.value,
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
          badgeCount: getAllUnreadCount.value,
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
          badgeCount: getMentionsUnreadCount.value,
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
          badgeCount: getParticipatingUnreadCount.value,
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
          badgeCount: getUnattendedUnreadCount.value,
        },
        ...(conversationCustomViews.value.length
          ? [
              {
                name: 'Folders',
                label: t('SIDEBAR.OTHER_FILTERS'),
                icon: 'i-lucide-list-filter',
                activeOn: ['conversations_through_folders'],
                children: conversationCustomViews.value.map(view => ({
                  name: `${view.name}-${view.id}`,
                  pinKey: `folder:${view.id}`,
                  label: view.name,
                  badgeCount: getCustomFilterUnreadCount.value(view.id),
                  to: accountScopedRoute('folder_conversations', {
                    id: view.id,
                  }),
                })),
              },
            ]
          : []),
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            pinKey: `inbox:${inbox.id}`,
            label: inbox.name,
            badgeCount: getInboxUnreadCount.value(inbox.id),
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
                badgeCount: leafProps.badgeCount,
              }),
          })),
        },
      ],
    },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      tag: 'Beta',
      activeOn: ['captain_assistants_create_index'],
      children: [
        {
          name: 'Company',
          label: t('SIDEBAR.CAPTAIN_COMPANY'),
          activeOn: ['captain_assistants_company_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_company_index',
          }),
        },
        {
          name: 'Behavior',
          label: t('SIDEBAR.CAPTAIN_BEHAVIOR'),
          activeOn: ['captain_assistants_behavior_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_behavior_index',
          }),
        },
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: ['captain_assistants_responses_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.CAPTAIN_INBOXES'),
          activeOn: ['captain_assistants_inboxes_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_inboxes_index',
          }),
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
        },
        // {
        //   name: 'PromptVersions',
        //   label: t('SIDEBAR.CAPTAIN_PROMPT_VERSIONS'),
        //   activeOn: ['captain_assistants_prompt_versions_index'],
        //   to: accountScopedRoute('captain_assistants_index', {
        //     navigationPath: 'captain_assistants_prompt_versions_index',
        //   }),
        // },
      ],
    },
    {
      name: 'Lists',
      label: t('SIDEBAR.LISTS'),
      icon: 'i-lucide-list',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.CONTACTS'),
          icon: 'i-lucide-contact',
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index'],
        },
        {
          name: 'All Companies',
          label: t('SIDEBAR.COMPANIES'),
          icon: 'i-lucide-building',
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
        {
          name: 'Products',
          label: t('SIDEBAR.PRODUCTS'),
          icon: 'i-lucide-package',
          to: accountScopedRoute('products_dashboard_index'),
          activeOn: ['products_dashboard_index'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            pinKey: `segment:${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: ['contacts_dashboard_segments_index'],
          })),
        },
      ],
    },
    {
      name: 'Deals',
      label: t('SIDEBAR.DEALS'),
      icon: 'i-lucide-dollar-sign',
      children: pipelineMenuChildren.value,
    },
    {
      name: 'Calendar',
      label: t('SIDEBAR.CALENDAR'),
      icon: 'i-lucide-calendar',
      to: accountScopedRoute('calendar_index'),
      activeOn: ['calendar_index'],
      getterKeys: {
        count: 'taskStats/getBadgeCount',
      },
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          // Spec 137's unified list (campaigns + scheduled messages, one
          // page) - scheduled_messages_index (spec 09's standalone page)
          // stays reachable by direct URL but is no longer the menu target.
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        {
          name: 'Audiences',
          label: t('SIDEBAR.AUDIENCES'),
          to: accountScopedRoute('audiences_index'),
        },
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
      ],
    },
    {
      name: 'Automation V2',
      label: t('SIDEBAR.AUTOMATION_V2'),
      icon: 'i-lucide-workflow',
      to: accountScopedRoute('automation_v2_list'),
      activeOn: ['automation_v2_list'],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-mailbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Pipelines',
          label: t('SIDEBAR.PIPELINES'),
          icon: 'i-lucide-kanban',
          to: accountScopedRoute('pipelines_settings_index'),
        },
        {
          name: 'Settings Forms',
          label: t('SIDEBAR.FORMS'),
          icon: 'i-lucide-clipboard-list',
          activeOn: [
            'forms_settings_index',
            'forms_settings_new',
            'forms_settings_edit',
            'forms_settings_submissions',
          ],
          to: accountScopedRoute('forms_settings_index'),
        },
        {
          name: 'Settings Commercial Goals',
          label: t('SIDEBAR.COMMERCIAL_GOALS'),
          icon: 'i-lucide-target',
          to: accountScopedRoute('commercial_goals_settings_index'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
    ...(isSupportAccount.value
      ? []
      : [
          {
            name: 'Support',
            label: t('SIDEBAR.SUPPORT'),
            icon: 'i-lucide-circle-help',
            to: accountScopedRoute('support_view'),
            activeOn: ['support_view'],
          },
        ]),
  ]
    .filter(item => !HIDDEN_MENU_ITEMS.includes(item.name))
    .map(item =>
      item.children
        ? {
            ...item,
            children: item.children.filter(
              child => !HIDDEN_MENU_CHILDREN.includes(child.name)
            ),
          }
        : item
    );
});

// Parte de `menuItems` (já sem os itens ocultos), então o que está oculto não pode ser fixado
const pinnableItems = computed(() => flattenPinnableItems(menuItems.value));
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-gradient-to-b from-n-slate-2 to-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex items-center min-w-0 gap-2"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div
            class="grid flex-shrink-0 place-content-center size-6 select-none"
            @click="registerLogoClick"
          >
            <Logo class="size-4 object-contain" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow min-w-0 -mx-1"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
          <SidebarNotificationBell />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <SidebarNotificationBell v-if="isEffectivelyCollapsed" />
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-2 py-1 transition-all duration-100 ease-out rounded-full h-8 outline outline-1 outline-n-weak bg-n-button-color"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center transition-all duration-100 ease-out rounded-full size-8 outline outline-1 outline-n-weak bg-n-button-color hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
      </div>
    </section>
    <nav
      class="grid content-start flex-grow min-w-0 gap-2 pb-5 overflow-y-scroll no-scrollbar"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <SidebarPinnedSection
        :items="pinnableItems"
        :collapsed="isEffectivelyCollapsed"
      />
      <ul
        class="flex flex-col min-w-0 gap-1 m-0 list-none"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="relative flex flex-col items-center justify-between flex-shrink-0 gap-1"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="absolute top-0 z-40 hidden w-1 h-full md:block cursor-col-resize ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 w-px h-full transition-colors bg-transparent ltr:right-0 rtl:left-0 group-hover:bg-n-brand"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
